import { useRef, useState } from 'react';
import { useAppDispatch } from '@store';
import {
  useAddCofApplicationDocumentsMutation,
  useAddIndividualApplicationDocumentsMutation,
  useBatchDeleteCOFApplicationDocumentsMutation,
  useBatchDeleteIndividualApplicationDocumentsMutation,
} from '../../../../hoc/new-application/store/services';
import { useCustomersUploadMutation } from '../../../../store/documents/services';
import { useCheckDocumentStringNameMutation } from '../../../../../../../store/service';
import { UploadResponse } from '../../../../store/documents/types';
import { ApplicationDocument } from '../../../../hoc/new-application/types';
import { deleteIsSuccess } from '../../../../hoc/new-application/individual/store/slices';
import { deleteIsSuccess as cofDeleteIsSuccess } from '../../../../hoc/new-application/main-representative/store/slices';

const useGroupInformation = ({
  isCof,
  applicationId,
  groupStructurChartFile,
  documents,
}: {
  isCof?: boolean;
  applicationId?: string;
  groupStructurChartFile?: ApplicationDocument;
  documents?: ApplicationDocument[];
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [uploadedFileError, setUploadedFilesError] = useState<string>('');

  const dispatch = useAppDispatch();

  const [deleteIndividualAppicationDocuments, { isLoading: deleteInProgress }] =
    useBatchDeleteIndividualApplicationDocumentsMutation();
  const [deleteCofAppicationDocuments, { isLoading: cofDeleteInProgress }] =
    useBatchDeleteCOFApplicationDocumentsMutation();

  const [
    addIndividualApplicationDocument,
    { isLoading: documentSubmitInProgress },
  ] = useAddIndividualApplicationDocumentsMutation();
  const [
    addCofApplicationDocument,
    { isLoading: cofDocumentSubmitInProgress },
  ] = useAddCofApplicationDocumentsMutation();

  const [upload, { isLoading: fileUploadInProgress }] =
    useCustomersUploadMutation();
  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckDocumentStringNameMutation();

  const onUpload = async (files: FileList) => {
    setUploadedFilesError('');

    const file = files[0];

    const checkNameResponse = (await checkDocumentName({
      value: file.name,
    })) as {
      error?: {
        data: {
          message?: {
            constraints: Record<string, string>;
          }[];
        };
      };
    };

    let nextFile;

    if (!checkNameResponse?.error) {
      const response = (await upload(file)) as unknown as {
        data?: UploadResponse[];
        error?: {
          originalStatus: number;
          data?: {
            message?: string;
          };
        };
        message?: string;
      };

      if (response && response.data) {
        nextFile = { ...response.data[0] };

        let fileName = file.name;

        if (response.data[0] && response.data[0]?.mimeType?.includes('image')) {
          fileName = `${file?.name.slice(0, file?.name.lastIndexOf('.'))}.jpg`;
        }

        nextFile.name = fileName;

        const body: {
          uploadId: string;
          name: string;
          documentCategoryId?: string;
        } = {
          uploadId: nextFile.id,
          name: nextFile.name,
          documentCategoryId: '72b810fc-d635-4937-8a14-f9b77eaa699f',
        };

        let documentResponse;

        if (isCof) {
          documentResponse = (await addCofApplicationDocument({
            id: applicationId as string,
            body,
          })) as {
            error?: {
              data: {
                message?: {
                  constraints: Record<string, string>;
                }[];
              };
            };
          };
        } else {
          documentResponse = (await addIndividualApplicationDocument({
            id: applicationId as string,
            body,
          })) as {
            error?: {
              data: {
                message?: {
                  constraints: Record<string, string>;
                }[];
              };
            };
          };
        }

        if (
          documentResponse.error &&
          documentResponse?.error?.data?.message?.length
        ) {
          const errorItem =
            documentResponse?.error?.data?.message[0].constraints;

          if (errorItem) {
            const [documentValue] = Object.values(errorItem);

            setUploadedFilesError(documentValue);
          }
        }
      } else if (response.error) {
        if (response.error.originalStatus === 413) {
          setUploadedFilesError(`${file.name} size too large`);
        } else {
          // eslint-disable-next-line no-lonely-if
          if (response?.error?.data?.message) {
            setUploadedFilesError(response?.error?.data?.message);
          }
        }
      }
    } else if (
      checkNameResponse?.error &&
      checkNameResponse?.error?.data?.message
    ) {
      const errorItem = checkNameResponse?.error?.data?.message[0]?.constraints;

      if (errorItem) {
        setUploadedFilesError(`${file.name} ${Object.values(errorItem)[0]}`);
      }
    }

    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const onDeleteDocuments = async () => {
    let response;

    if (isCof) {
      response = await deleteCofAppicationDocuments({
        id: applicationId as string,
        documentId: [groupStructurChartFile?.id as string],
      });
    } else {
      response = await deleteIndividualAppicationDocuments({
        id: applicationId as string,
        documentId: [groupStructurChartFile?.id as string],
      });
    }

    if (response) {
      const nextDocuments = documents?.filter(
        (document) => document?.id !== groupStructurChartFile?.id,
      );

      if (isCof) {
        dispatch(cofDeleteIsSuccess(nextDocuments as ApplicationDocument[]));
      } else {
        dispatch(deleteIsSuccess(nextDocuments as ApplicationDocument[]));
      }
    }
  };

  return {
    fileInputRef,
    onUpload,
    documentSubmitInProgress,
    fileUploadInProgress,
    checkDocumentNameInProgress,
    cofDocumentSubmitInProgress,
    deleteInProgress,
    cofDeleteInProgress,
    onDeleteDocuments,
    uploadedFileError,
  };
};

export default useGroupInformation;
